/* global BigInt */
import pako from 'pako'

// ─── SDP Compaction ───────────────────────────────────────────────────────────

// Helper to check for real IPv4 addresses (not mDNS .local)
const isRealIPv4 = (addr) =>
  addr &&
  !addr.endsWith('.local') &&
  !addr.includes(':') &&
  /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(addr)

const ESSENTIAL_PREFIXES = [
  'v=', 'o=', 's=', 't=',
  'a=group:', 'a=msid-semantic',
  'm=application', 'm=video', 'm=audio',
  'c=',
  'a=ice-ufrag', 'a=ice-pwd', 'a=ice-options',
  'a=fingerprint', 'a=setup', 'a=mid',
  'a=sendonly', 'a=recvonly', 'a=sendrecv', 'a=inactive',
  'a=sctp-port', 'a=max-message-size',
  'a=rtcp-mux', 'a=rtcp-rsize',
  'a=rtpmap', 'a=fmtp', 'a=rtcp-fb',
  'a=extmap',
]

export const compactSDP = (sdp) => {
  let hostCandidateAdded = false
  let srflxCandidateAdded = false
  const lines = sdp.split(/\r\n|\n/).filter(l => l.trim())
  const kept = []

  for (const line of lines) {
    if (line.startsWith('m=')) {
      hostCandidateAdded = false
      srflxCandidateAdded = false
      kept.push(line)
      continue
    }
    let matched = false
    for (const prefix of ESSENTIAL_PREFIXES) {
      if (line.startsWith(prefix)) { matched = true; break }
    }
    if (matched) { kept.push(line); continue }

    if (line.startsWith('a=candidate')) {
      const parts = line.split(' ')
      const type  = parts[7]
      const addr  = parts[4] || ''
      const port  = parseInt(parts[5], 10)
      if (port <= 0) continue

      if (type === 'host' && !hostCandidateAdded) {
        if (isRealIPv4(addr)) {
          hostCandidateAdded = true
          kept.push(line)
        }
        // skip mDNS .local — a real-IP host or srflx below will be used
        continue
      }
      if (type === 'srflx' && !srflxCandidateAdded) {
        srflxCandidateAdded = true
        kept.push(line)
        continue
      }
    }
  }

  // Fallback: If no real-IP host candidate was found, fall back to mDNS (same-device only)
  const hasHostCandidate = kept.some(l => l.startsWith('a=candidate') && l.includes('typ host'))
  if (!hasHostCandidate) {
    for (const line of lines) {
      if (!line.startsWith('a=candidate')) continue
      const parts = line.split(' ')
      if (parts[7] === 'host' && parseInt(parts[5], 10) > 0) {
        kept.push(line)
        break
      }
    }
  }

  return kept.join('\r\n') + '\r\n'
}

export const encodeForQR = (sdp) => {
  try {
    const compact    = compactSDP(sdp)
    const compressed = pako.deflateRaw(compact, { level: 9 })
    let binary = ''
    const chunk = 8192
    for (let i = 0; i < compressed.length; i += chunk)
      binary += String.fromCharCode.apply(null, compressed.subarray(i, i + chunk))
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  } catch (e) { console.warn('[encodeForQR] failed:', e.message); return null }
}

export const decodeFromQR = (encoded) => {
  try {
    const b64   = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const pad   = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const bin   = atob(b64 + pad)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const inflated = pako.inflateRaw(bytes)
    let result = ''
    const chunk = 8192
    for (let i = 0; i < inflated.length; i += chunk)
      result += String.fromCharCode.apply(null, inflated.subarray(i, i + chunk))
    return result
  } catch (e) { console.warn('[decodeFromQR] failed:', e.message); return null }
}

export const estimateEncodedSize = (sdp) => {
  try { return Math.ceil(pako.deflateRaw(compactSDP(sdp), { level: 9 }).length * 4 / 3) } catch { return Infinity }
}

export const QR_SAFE_LIMIT = 2800

// ─── ICE Candidate extraction ─────────────────────────────────────────────────

export const extractCandidateFromSDP = (sdp) => {
  const lines = sdp.split(/\r\n|\n/)

  // Pass 1: real IPv4 host candidate (best for local network)
  for (const line of lines) {
    if (!line.startsWith('a=candidate')) continue
    const p = line.split(' ')
    const type = p[7], addr = p[4] || '', port = parseInt(p[5], 10)
    if (type === 'host' && port > 0 && isRealIPv4(addr)) return { ip: addr, port }
  }

  // Pass 2: srflx — server-reflexive, works across networks
  for (const line of lines) {
    if (!line.startsWith('a=candidate')) continue
    const p = line.split(' ')
    const type = p[7], addr = p[4] || '', port = parseInt(p[5], 10)
    if (type === 'srflx' && port > 0 && isRealIPv4(addr)) return { ip: addr, port }
  }

  // Pass 3: any host including mDNS — same-device last resort
  for (const line of lines) {
    if (!line.startsWith('a=candidate')) continue
    const p = line.split(' ')
    const type = p[7], addr = p[4] || '', port = parseInt(p[5], 10)
    if (type === 'host' && port > 0) return { ip: addr, port }
  }

  return null
}

export const extractFingerprintFromSDP = (sdp) => {
  const match = sdp.match(/a=fingerprint:sha-256 ([\dA-Fa-f:]+)/)
  if (!match) return null
  return match[1].replace(/:/g, '').toUpperCase()
}

export const extractMidsFromSDP = (sdp) => {
  const mids = []
  for (const line of sdp.split(/\r\n|\n/)) {
    const m = line.match(/^a=mid:(.+)$/)
    if (m) mids.push(m[1].trim())
  }
  return mids.length ? mids : ['0']
}

export const extractMlinesFromSDP = (sdp) => {
  const lines  = sdp.split(/\r\n|\n/)
  const result = []
  let currentType = null
  for (const line of lines) {
    const mMatch = line.match(/^m=(\S+)/)
    if (mMatch) { currentType = mMatch[1]; continue }
    const midMatch = line.match(/^a=mid:(.+)$/)
    if (midMatch && currentType) {
      result.push({ mid: midMatch[1].trim(), type: currentType })
      currentType = null
    }
  }
  return result.length ? result : [{ mid: '0', type: 'application' }]
}

// ─── Answer SDP reconstruction ────────────────────────────────────────────────
// Builds a minimal but spec-compliant WebRTC answer SDP.
// Every line MUST end with \r\n — browsers are strict about this.

export const reconstructAnswerSDP = ({ ufrag, pwd, ip, port, fingerprintHex, mids = ['0'], mlines = null }) => {
  if (!ufrag || !pwd || !ip || !port) {
    console.error('[reconstructAnswerSDP] missing required fields', { ufrag, pwd, ip, port })
    throw new Error('Missing required SDP fields')
  }

  // Format fingerprint as colon-separated uppercase pairs
  const rawHex = (fingerprintHex || '').replace(/:/g, '').toUpperCase()
  const fp = rawHex.length === 64
    ? `sha-256 ${rawHex.match(/.{2}/g).join(':')}`
    : 'sha-256 00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00'

  const typeMap = {}
  if (mlines) mlines.forEach(({ mid, type }) => { typeMap[mid] = type })

  const orderedMids = mlines ? mlines.map(m => m.mid) : mids

  // Build each m-section as an array of lines, then join with \r\n
  const buildSection = (mid) => {
    const type    = typeMap[mid] || 'application'
    const isVideo = type === 'video'
    const isAudio = type === 'audio'

    const common = [
      'c=IN IP4 0.0.0.0',
      `a=ice-ufrag:${ufrag}`,
      `a=ice-pwd:${pwd}`,
      'a=ice-options:trickle',
      `a=fingerprint:${fp}`,
      'a=setup:active',
      `a=mid:${mid}`,
      `a=candidate:1 1 UDP 2130706431 ${ip} ${port} typ host generation 0`,
      'a=end-of-candidates',
    ]

    if (isVideo) {
      return [
        'm=video 9 UDP/TLS/RTP/SAVPF 96',
        ...common,
        'a=recvonly',
        'a=rtpmap:96 H264/90000',
        'a=rtcp-mux',
      ]
    } else if (isAudio) {
      return [
        'm=audio 9 UDP/TLS/RTP/SAVPF 111',
        ...common,
        'a=recvonly',
        'a=rtpmap:111 opus/48000/2',
        'a=rtcp-mux',
      ]
    } else {
      return [
        'm=application 9 UDP/DTLS/SCTP webrtc-datachannel',
        ...common,
        'a=sctp-port:5000',
        'a=max-message-size:262144',
      ]
    }
  }

  const header = [
    'v=0',
    'o=- 0 2 IN IP4 127.0.0.1',
    's=-',
    't=0 0',
    `a=group:BUNDLE ${orderedMids.join(' ')}`,
    'a=msid-semantic: WMS',
  ]

  const allLines = [
    ...header,
    ...orderedMids.flatMap(mid => buildSection(mid)),
  ]

  // Every line must end with \r\n — spec requirement
  return allLines.join('\r\n') + '\r\n'
}

// ─── Legacy helpers ───────────────────────────────────────────────────────────

const BASE64_ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
const BASE36_ALPHA = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const HKDF_SALT    = new TextEncoder().encode('webrtc-studio-v1')

export const generateSessionKey = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(8))
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
}

export const deriveICECredentials = async (sessionKeyHex) => {
  const keyBytes = new Uint8Array(sessionKeyHex.match(/.{2}/g).map(h => parseInt(h, 16)))
  const baseKey  = await crypto.subtle.importKey('raw', keyBytes, 'HKDF', false, ['deriveBits'])
  const derive   = async (info, len) => {
    const bits = await crypto.subtle.deriveBits(
      { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT, info: new TextEncoder().encode(info) },
      baseKey, len * 8)
    return new Uint8Array(bits)
  }
  const uBytes = await derive('ice-ufrag', 4)
  const pBytes = await derive('ice-pwd',   22)
  const toICE  = (buf) => Array.from(buf).map(b => BASE64_ALPHA[b % 64]).join('')
  return { ufrag: toICE(uBytes), pwd: toICE(pBytes) }
}

export const encodeAnswerCode = (ip, port) => {
  const parts = ip.split('.').map(Number)
  const buf   = new Uint8Array(6)
  buf[0] = parts[0]; buf[1] = parts[1]; buf[2] = parts[2]; buf[3] = parts[3]
  buf[4] = (port >> 8) & 0xff; buf[5] = port & 0xff
  const hex = Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('')
  let num = BigInt('0x' + hex), b36 = ''
  while (num > 0n) { b36 = BASE36_ALPHA[Number(num % 36n)] + b36; num /= 36n }
  return b36.padStart(12, '0').match(/.{4}/g).join('-')
}

export const decodeAnswerCode = (code) => {
  try {
    const clean = code.replace(/-/g, '').replace(/\s/g, '').toUpperCase()
    if (clean.length !== 12) return null
    let num = 0n
    for (const c of clean) {
      const idx = BASE36_ALPHA.indexOf(c)
      if (idx === -1) return null
      num = num * 36n + BigInt(idx)
    }
    const buf = new Uint8Array(6)
    for (let i = 5; i >= 0; i--) { buf[i] = Number(num & 0xffn); num >>= 8n }
    const ip   = `${buf[0]}.${buf[1]}.${buf[2]}.${buf[3]}`
    const port = (buf[4] << 8) | buf[5]
    if (buf[0] === 0 && buf[1] === 0 && buf[2] === 0 && buf[3] === 0) return null
    if (port === 0) return null
    return { ip, port }
  } catch { return null }
}

export const formatAnswerCode = (raw) => raw.replace(/-/g, '').toUpperCase().padStart(12,'0').match(/.{4}/g).join('-')
export const encodeFPForQR    = (fingerprintHex) => `fp:${fingerprintHex.toUpperCase().replace(/:/g, '')}`
export const decodeFPFromQR   = (data) => {
  if (!data?.startsWith('fp:')) return null
  const hex = data.slice(3).replace(/:/g, '').toUpperCase()
  return hex.length === 64 ? hex : null
}